import {
  Document,
  Page,
  StyleSheet,
  Text,
  View
} from "@react-pdf/renderer"
import { jwtDecode } from "jwt-decode"
import moment from "moment"

function companyNameFromToken(): string {
  try {
    const token = localStorage.getItem("token")
    if (!token) return "-"
    const d = jwtDecode<{ company_name?: string }>(token)
    return d.company_name ?? "-"
  } catch {
    return "-"
  }
}

function splitRowsForPages(
  rows: string[][],
  firstPageCount: number,
  otherPageCount: number
): string[][][] {
  if (rows.length === 0) return [[]]
  const pages: string[][][] = []
  pages.push(rows.slice(0, firstPageCount))
  let i = firstPageCount
  while (i < rows.length) {
    pages.push(rows.slice(i, i + otherPageCount))
    i += otherPageCount
  }
  return pages
}

const styles = StyleSheet.create({
  page: {
    paddingHorizontal: 20,
    paddingTop: 26,
    paddingBottom: 44,
    fontFamily: "Helvetica"
  },
  title: { fontSize: 13, fontWeight: "bold" },
  subtitle: { fontSize: 8, marginTop: 3, color: "#333" },
  kpiWrap: { marginTop: 8, marginBottom: 10 },
  kpiRow: { flexDirection: "row" },
  kpiHeadCell: {
    flex: 1,
    backgroundColor: "#050505",
    color: "#ffffff",
    fontSize: 7,
    fontWeight: "bold",
    paddingVertical: 4,
    paddingHorizontal: 2,
    textAlign: "center",
    borderWidth: 1,
    borderColor: "#666"
  },
  kpiBodyCell: {
    flex: 1,
    fontSize: 8,
    fontWeight: "bold",
    paddingVertical: 5,
    paddingHorizontal: 2,
    textAlign: "center",
    borderWidth: 1,
    borderColor: "#999"
  },
  tableHeadRow: { flexDirection: "row", backgroundColor: "#050505" },
  tableHeadCell: {
    paddingVertical: 3,
    paddingHorizontal: 2,
    borderWidth: 1,
    borderColor: "#555"
  },
  tableBodyRow: { flexDirection: "row" },
  tableBodyCell: {
    paddingVertical: 2,
    paddingHorizontal: 2,
    borderWidth: 1,
    borderColor: "#bbb"
  },
  footer: {
    position: "absolute",
    bottom: 10,
    left: 20,
    right: 20,
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 7,
    color: "#444"
  }
})

export type InventoryReportPdfProps = {
  orientation: "portrait" | "landscape"
  documentTitle: string
  heading: string
  subtitleLines: string[]
  kpiHeaders: string[]
  kpiValues: string[]
  tableHeaders: string[]
  tableRows: string[][]
  columnFlex: number[]
  columnAlign?: ("left" | "right" | "center")[]
  firstPageRowLimit: number
  otherPageRowLimit: number
}

export function InventoryReportPdfDocument({
  orientation,
  documentTitle,
  heading,
  subtitleLines,
  kpiHeaders,
  kpiValues,
  tableHeaders,
  tableRows,
  columnFlex,
  columnAlign,
  firstPageRowLimit,
  otherPageRowLimit
}: InventoryReportPdfProps) {
  const pages = splitRowsForPages(
    tableRows,
    firstPageRowLimit,
    otherPageRowLimit
  )
  const company = companyNameFromToken()
  const showKpi = kpiHeaders.length > 0 && kpiValues.length > 0

  return (
    <Document title={documentTitle}>
      {pages.map((pageRows, pageIndex) => (
        <Page
          key={`p-${pageIndex}`}
          size="A4"
          orientation={orientation}
          style={styles.page}
          wrap>
          {pageIndex === 0 ? (
            <>
              <Text style={styles.title}>{heading}</Text>
              {subtitleLines.map((line, i) => (
                <Text key={`sub-${i}`} style={styles.subtitle}>
                  {line}
                </Text>
              ))}
              {showKpi ? (
                <View style={styles.kpiWrap}>
                  <View style={styles.kpiRow}>
                    {kpiHeaders.map((h, i) => (
                      <Text key={`kh-${i}`} style={styles.kpiHeadCell}>
                        {h}
                      </Text>
                    ))}
                  </View>
                  <View style={styles.kpiRow}>
                    {kpiValues.map((v, i) => (
                      <Text key={`kv-${i}`} style={styles.kpiBodyCell}>
                        {v}
                      </Text>
                    ))}
                  </View>
                </View>
              ) : null}
            </>
          ) : null}

          <View style={styles.tableHeadRow}>
            {tableHeaders.map((h, i) => {
              const flexN = columnFlex[i] ?? 1
              const align = columnAlign?.[i] ?? "left"
              return (
                <View
                  key={`th-${i}`}
                  style={[styles.tableHeadCell, { flex: flexN }]}>
                  <Text
                    style={{
                      fontSize: 5.5,
                      fontWeight: "bold",
                      color: "#ffffff",
                      textAlign: align
                    }}
                    wrap>
                    {h}
                  </Text>
                </View>
              )
            })}
          </View>

          {pageRows.map((row, ri) => (
            <View
              key={`${pageIndex}-r-${ri}`}
              style={styles.tableBodyRow}
              wrap={false}>
              {row.map((cell, ci) => {
                const flexN = columnFlex[ci] ?? 1
                const align = columnAlign?.[ci] ?? "left"
                return (
                  <View
                    key={`${pageIndex}-r-${ri}-c-${ci}`}
                    style={[styles.tableBodyCell, { flex: flexN }]}>
                    <Text
                      style={{ fontSize: 5, textAlign: align, color: "#111111" }}
                      wrap>
                      {cell}
                    </Text>
                  </View>
                )
              })}
            </View>
          ))}

          <View style={styles.footer} fixed>
            <Text>
              {heading} | {company} | {moment().format("DD MMM YYYY")}
            </Text>
            <Text
              render={({ pageNumber, totalPages }) =>
                `Page ${pageNumber} of ${totalPages}`
              }
            />
          </View>
        </Page>
      ))}
    </Document>
  )
}
